import { BucketState } from "@omegagrid/bucket";
import { AccountingProvider } from "../client/accountingProvider";
import utf8 from "utf8";
import qs from 'qs';

export type RouterState = {
	a?: number;
	app?: string;
}

export type RouterOptions = {
	apps?: string[], // path prefixes to ignore during path parsing
	anonymousApps?: string[], // apps a guest (no session) may open, see anonymousAccess
	settingsPrefix: string,
	baseUrl?: string
}

export class RouterEvent extends Event {
	constructor(type: string, public readonly state: RouterState) {
		super(type);
	}
}

const DEFAULT_ROUTER_OPTIONS: RouterOptions = {
	settingsPrefix: 'rp',
	apps: []
};

export class Router extends EventTarget {

	private static popstateListener: () => void;

	private url: URL;
	private _path: string[];
	get path() { return this._path }
	private _initialHash: string;
	/**
	 * The fragment of the address the page opened with (without '#') when it is an anchor for the app and
	 * not a host state: a guest's address never carries the state (getUrl), a user's does once the host
	 * has one, so a fragment that decodes as a state is not an anchor.
	 */
	get initialAnchor() {
		const hash = this._initialHash.replace(/^#/, '');
		return !hash || (!this.anonymousAccess && Router.decodeState(hash)) ? '' : hash;
	}
	private _guest = false;
	/** the page is served to a guest (no session); set by the application once the session is known, see anonymousAccess */
	get guest() { return this._guest }
	set guest(guest: boolean) {
		this._guest = guest;
		// what the address carries depends on it (hostState), so the state read so far does not count
		this._hostState = null;
		this._hostStateHash = null;
	}
	private _options: RouterOptions;
	private _state: RouterState = {};
	private _hostState: BucketState;
	private _hostStateHash: string;
	private _afterInit = false;
	private _accountingProvider: AccountingProvider;
	get accountingProvider() { return this._accountingProvider }

	get options() { return this._options }
	get accounting() { return this._accountingProvider.a }
	get afterInit() { return this._afterInit }
	// setState sets the company of the accounting provider and, when it changes, drops the host state read
	// so far (it belongs to the company the address named, or to none)
	set accounting(a: number) {
		this.setState({...this.state, a}, false);
	}
	get app() { return this.state.app }
	get state() { return this._state }

	set hash(value: string) {
		this.url.hash = value;
	}

	private _queryParams: Record<string, unknown>;
	get queryParams() { return this._queryParams || {} }
	get queryString() {
		return qs.stringify(this._queryParams || {}, {addQueryPrefix: true});
	}

	qp<T = unknown>(key: string, defaultValue?: T): T {
		const v = this._queryParams?.[key];
		return v == null || v === '' ? defaultValue : (v as T);
	}

	public setState(state: RouterState, dispatchEvent = true) {
		this._state = state;
		if (this._accountingProvider.a != this.state.a) {
			this._accountingProvider.a = this.state.a;
			this._hostState = null;
			this._hostStateHash = null;
			this.hash = '';
		}
		history.replaceState(this.state, 'Reporting', this.getUrl());
		if (dispatchEvent) {
			this.dispatchEvent(new RouterEvent('route', state));
		}
	}

	get hostStateHash() {
		if (this._hostStateHash) return this._hostStateHash;
		if (this.hostState) {
			try {
				this._hostStateHash = btoa(utf8.encode(JSON.stringify(this.hostState)));
			} catch (error) {
				console.error(error);
			}
		}
		return this._hostStateHash;
	}

	get hostState() {
		if (this._hostState) return this._hostState;
		// the fragment carries the state unless it is an anchor for the app (initialAnchor; a guest's address
		// never carries the state, see getUrl): then the state stored for the company applies
		const fromUrl = this.anonymousAccess ? null : Router.decodeState(this.url.hash);
		this._hostState = fromUrl ?? Router.decodeState(localStorage.getItem(`${this._options.settingsPrefix}-state-${this.state.a || 0}`));
		return this._hostState;
	}

	/** The host state a fragment or a stored value encodes (base64 JSON), null for anything else (e.g. an anchor). */
	private static decodeState(base64State: string | null): BucketState | null {
		if (!base64State) return null;
		try {
			const state = JSON.parse(utf8.decode(atob(base64State.replace(/^#/, ''))));
			return state && typeof state === 'object' && ('layout' in state || 'sidebar' in state) ? state : null;
		} catch (error) {
			return null;
		}
	}

	/** the address names an app a guest may open (anonymousApps) */
	get anonymousApp() {
		return !!this.options?.anonymousApps?.includes(this.app);
	}

	/**
	 * The app is served to a guest: the address names an app a guest may open and there is no session
	 * (guest). Such an address carries neither the company nor the host state (getUrl) and the host shows
	 * one component without the side menu; a user opening the same address gets the application as usual,
	 * with the page the address names opened in it.
	 */
	get anonymousAccess() {
		return this._guest && this.anonymousApp;
	}

	set hostState(state: BucketState|null) {
		this._hostState = state;
		this._hostStateHash = null;
		const key = `${this._options.settingsPrefix}-state-${this.state.a || 0}`;
		if (!state) {
			localStorage.removeItem(key);
			this.hash = '';
		} else {
			localStorage.setItem(`${this._options.settingsPrefix}-state-${this.state.a || 0}`, this.hostStateHash);
		}
		history.replaceState(this.state, 'Reporting', this.getUrl());
	}

	clearHostState() {
		this.hostState = null;
	}

	getUrl(state?: RouterState): string {
		let baseUrl = this._options?.baseUrl;
		if (!baseUrl || baseUrl == '/') baseUrl = `${this.url.protocol}//${this.url.hostname}`;

		let hash = this.hostStateHash;
		if (state?.a != null && state.a !== this.state.a) {
			hash = localStorage.getItem(`${this._options.settingsPrefix}-state-${state.a || 0}`) || '';
		}

		return [
			baseUrl,
			'/',
			this.getPath(state),
			this.queryString,
			hash && !this.anonymousAccess ? `#${hash}` : ''
		].join('');
	}

	getPath(state?: RouterState) {
		state ??= this.state;
		const parts = [];
		if (state.a && !this.anonymousAccess) parts.push(state.a.toString());
		if (state.app) parts.push(state.app);
		parts.push(...this._path);
		return parts.join('/');
	}

	constructor(url: string, accountingProvider: AccountingProvider, options?: Partial<RouterOptions>) {
		super();
		this._accountingProvider = accountingProvider;
		this._options = {...DEFAULT_ROUTER_OPTIONS, ...options};
		this.url = new URL(url);
		this._initialHash = this.url.hash;
		this._queryParams = qs.parse(this.url.search, {ignoreQueryPrefix: true});
		if (this._queryParams['init'] === '1') {
			this._afterInit = true;
			delete this._queryParams['init'];
		}

		const {a, app, path} = this.parsePath(this.url.pathname);
		// the path below the app is the app's to interpret (openPath); without an app it names nothing
		// (e.g. a stray address) and would only be carried along in every address written from now on
		this._path = app != null ? path : [];
		if (a != null) this._accountingProvider.a = a;

		this.setState({a: a == null ? null : a, app: app} as RouterState, false);

		// back/forward between entries added by setPath(): only the path below the app changes;
		// the router is recreated on every route, so only the latest one listens
		if (Router.popstateListener) window.removeEventListener('popstate', Router.popstateListener);
		Router.popstateListener = () => {
			this._path = this.parsePath(location.pathname).path;
			this.dispatchEvent(new RouterEvent('path', this.state));
		};
		window.addEventListener('popstate', Router.popstateListener);
	}

	/**
	 * Splits a URL path into accounting, app and the rest (segments stay URL-encoded).
	 * canonical form: /{accounting}/{app}/... — accounting (strict integer) always precedes app
	 */
	private parsePath(pathname: string): {a?: number, app?: string, path: string[]} {
		const path = pathname.split('/').filter(p => p.length > 0);
		let a: number, app: string;

		if (path.length > 0) {
			const first = path[0];
			if (/^-?\d+$/.test(first)) {
				a = parseInt(first);
				if (path.length > 1 && this._options.apps?.includes(path[1])) {
					app = path[1];
				}
			} else if (this._options.apps?.includes(first)) {
				app = first;
			}
		}

		if (a != null) path.shift();
		if (app != null) path.shift();
		return {a, app, path};
	}

	/**
	 * Replaces the path below the app (URL-encoded segments joined by '/') and records it in the
	 * browser history, so the address can be shared and back/forward works (see the popstate handler).
	 */
	setPath(path: string, push = true) {
		const segments = path.split('/').filter(p => p.length > 0);
		if (segments.join('/') === this._path.join('/')) return;
		this._path = segments;
		if (push) history.pushState(this.state, 'Reporting', this.getUrl());
		else history.replaceState(this.state, 'Reporting', this.getUrl());
	}

}
